/**
 * A song in the Library (W2-10 projects-data; DESIGN.md §4.1, §5.16): charts that use the same song file (music
 * SHA-256) are difficulties of one song. The header shows the song's illustration, title and artist and how many
 * difficulties share it; the cards follow.
 */

import type { ReactNode } from 'react';
import type { LibraryText } from './hooks';
import { m } from './library.messages';
import type { SongGroupModel } from './libraryModel';
import { Thumbnail } from './ProjectCard';
import s from './Library.module.css';

export interface SongGroupProps {
  readonly text: LibraryText;
  readonly group: SongGroupModel;
  readonly view: 'grid' | 'list';
  readonly children: ReactNode;
}

export function SongGroup(props: SongGroupProps) {
  const { text, group, view } = props;
  const title = group.title.trim() === '' ? text.t(m.untitled) : group.title;
  const composer = group.composer.trim() === '' ? text.t(m.unknownComposer) : group.composer;
  const cover = group.projects.find((p) => p.thumbnail !== null)?.thumbnail ?? null;
  const headingId = `song-${group.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  return (
    <section className={s.group} data-view={view} aria-labelledby={headingId}>
      <header className={s.groupHeader}>
        {view === 'list' && <Thumbnail blob={cover} alt="" className={s.groupThumb} />}
        <div className={s.groupText}>
          <h3 className={s.groupTitle} id={headingId}>
            <span className="visually-hidden">{text.t(m.songGroupLabel, { title })}</span>
            <span aria-hidden="true">{title}</span>
          </h3>
          <span className={s.groupComposer}>{composer}</span>
          {group.total > 1 && <span className={s.groupShared}>{text.t(m.songShared, { count: group.total })}</span>}
        </div>
      </header>
      <ul className={s.cards} data-view={view} role="list">
        {props.children}
      </ul>
    </section>
  );
}
